import type { PARInvoiceRow } from "@/lib/par/csvInvoiceParser";

export interface ChatInvoiceSummary {
  totalInvoices: number;
  totalOutstanding: number;
  totalPotentialSavings: number;
  vendorCount: number;
  earlyPayEligibleCount: number;
  earlyPayEligibleAmount: number;
}

export interface ChatTopVendor {
  name: string;
  outstanding: number;
  savings: number;
  discountPct: number;
}

export interface ChatSoonestDeadline {
  vendor: string;
  invoiceNum: string;
  amount: number;
  daysUntil: number;
  savings: number;
}

export interface ChatInvoiceRow {
  vendor: string;
  invoiceNum: string;
  amount: number;
  terms: string;
  earlyPayDue: string | null;
  discountPct: number | null;
  potentialSavings: number;
  dueDate: string;
}

export interface ChatTermsDistribution {
  terms: string;
  count: number;
  totalOutstanding: number;
}

export interface ChatVendorSummary {
  name: string;
  invoiceCount: number;
  totalOutstanding: number;
  totalSavings: number;
  avgDiscountPct: number;
  terms: string; // most common terms
}

export interface ChatPaymentRunSummary {
  id: string;
  name: string;
  status: string;
  budget: number;
  payDate: string;
  totalSavings: number;
  captureRate: number | null;
  vendors: string[];
}

export interface ChatContext {
  page: string;
  invoiceSummary: ChatInvoiceSummary;
  topVendors: ChatTopVendor[];
  soonestDeadlines: ChatSoonestDeadline[];
  invoiceRows: ChatInvoiceRow[];
  termsDistribution: ChatTermsDistribution[];
  vendorSummary: ChatVendorSummary[];
  paymentRuns?: ChatPaymentRunSummary[];
  contextVersion: string; // ISO timestamp — changes when invoice data changes
}

export function buildChatContext(
  invoices: PARInvoiceRow[],
  page: string,
  paymentRuns?: ChatPaymentRunSummary[]
): ChatContext {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Only unpaid invoices
  const unpaid = invoices.filter((r) => {
    const isPaid = !!r.paid_date || r.payment_status.toLowerCase().includes("paid");
    return !isPaid && r.amount_invoiced > r.amount_paid;
  });

  const totalOutstanding = unpaid.reduce((sum, r) => sum + r.amount_invoiced, 0);
  const totalPotentialSavings = unpaid.reduce((sum, r) => sum + (r.potential_savings ?? 0), 0);

  // Unique vendors
  const vendorNames = new Set(unpaid.map((r) => r.vendor_name));

  // Early pay eligible: has an early_pay_due_date in the future
  const earlyPayEligible = unpaid.filter((r) => {
    if (!r.early_pay_due_date) return false;
    const deadline = new Date(r.early_pay_due_date);
    return deadline >= today;
  });

  const earlyPayEligibleAmount = earlyPayEligible.reduce((sum, r) => sum + r.amount_invoiced, 0);

  // Top vendors by outstanding balance (max 5), also builds full vendor map for vendorSummary
  const vendorMap = new Map<
    string,
    {
      outstanding: number;
      savings: number;
      discountPct: number;
      count: number;
      termsCounts: Map<string, number>;
    }
  >();
  for (const r of unpaid) {
    const existing = vendorMap.get(r.vendor_name);
    const discountPct = r.discount_pct ?? 0;
    const terms = r.terms || "";
    if (existing) {
      existing.outstanding += r.amount_invoiced;
      existing.savings += r.potential_savings ?? 0;
      existing.discountPct =
        (existing.discountPct * existing.count + discountPct) / (existing.count + 1);
      existing.count++;
      existing.termsCounts.set(terms, (existing.termsCounts.get(terms) ?? 0) + 1);
    } else {
      const termsCounts = new Map<string, number>();
      termsCounts.set(terms, 1);
      vendorMap.set(r.vendor_name, {
        outstanding: r.amount_invoiced,
        savings: r.potential_savings ?? 0,
        discountPct,
        count: 1,
        termsCounts,
      });
    }
  }

  const topVendors: ChatTopVendor[] = Array.from(vendorMap.entries())
    .sort((a, b) => b[1].outstanding - a[1].outstanding)
    .slice(0, 5)
    .map(([name, data]) => ({
      name,
      outstanding: data.outstanding,
      savings: data.savings,
      discountPct: data.discountPct,
    }));

  // Soonest early pay deadlines (max 5)
  const soonestDeadlines: ChatSoonestDeadline[] = earlyPayEligible
    .filter((r) => r.early_pay_due_date)
    .sort((a, b) => {
      const da = new Date(a.early_pay_due_date).getTime();
      const db = new Date(b.early_pay_due_date).getTime();
      return da - db;
    })
    .slice(0, 5)
    .map((r) => {
      const deadline = new Date(r.early_pay_due_date);
      const daysUntil = Math.ceil((deadline.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      return {
        vendor: r.vendor_name,
        invoiceNum: r.invoice_number,
        amount: r.amount_invoiced,
        daysUntil,
        savings: r.potential_savings ?? 0,
      };
    });

  // Invoice rows: up to 150, early-pay eligible first, then by amount desc
  const eligibleSet = new Set(earlyPayEligible.map((r) => r.invoice_number + "|" + r.vendor_name));
  const sortedUnpaid = [...unpaid].sort((a, b) => {
    const aEligible = eligibleSet.has(a.invoice_number + "|" + a.vendor_name) ? 1 : 0;
    const bEligible = eligibleSet.has(b.invoice_number + "|" + b.vendor_name) ? 1 : 0;
    if (bEligible !== aEligible) return bEligible - aEligible;
    return b.amount_invoiced - a.amount_invoiced;
  });

  const invoiceRows: ChatInvoiceRow[] = sortedUnpaid.slice(0, 500).map((r) => ({
    vendor: r.vendor_name,
    invoiceNum: r.invoice_number,
    amount: r.amount_invoiced,
    terms: r.terms || "",
    earlyPayDue: r.early_pay_due_date || null,
    discountPct: r.discount_pct ?? null,
    potentialSavings: r.potential_savings ?? 0,
    dueDate: r.due_date || "",
  }));

  // Terms distribution: sorted by count desc
  const termsMap = new Map<string, { count: number; totalOutstanding: number }>();
  for (const r of unpaid) {
    const terms = r.terms || "Unknown";
    const existing = termsMap.get(terms);
    if (existing) {
      existing.count++;
      existing.totalOutstanding += r.amount_invoiced;
    } else {
      termsMap.set(terms, { count: 1, totalOutstanding: r.amount_invoiced });
    }
  }
  const termsDistribution: ChatTermsDistribution[] = Array.from(termsMap.entries())
    .sort((a, b) => b[1].count - a[1].count)
    .map(([terms, data]) => ({
      terms,
      count: data.count,
      totalOutstanding: data.totalOutstanding,
    }));

  // Vendor summary: all vendors sorted by outstanding desc
  const vendorSummary: ChatVendorSummary[] = Array.from(vendorMap.entries())
    .sort((a, b) => b[1].outstanding - a[1].outstanding)
    .map(([name, data]) => {
      // Find most common terms for this vendor
      let maxTermsCount = 0;
      let mostCommonTerms = "";
      for (const [t, cnt] of data.termsCounts.entries()) {
        if (cnt > maxTermsCount) {
          maxTermsCount = cnt;
          mostCommonTerms = t;
        }
      }
      return {
        name,
        invoiceCount: data.count,
        totalOutstanding: data.outstanding,
        totalSavings: data.savings,
        avgDiscountPct: data.discountPct,
        terms: mostCommonTerms,
      };
    });

  return {
    page,
    invoiceSummary: {
      totalInvoices: unpaid.length,
      totalOutstanding,
      totalPotentialSavings,
      vendorCount: vendorNames.size,
      earlyPayEligibleCount: earlyPayEligible.length,
      earlyPayEligibleAmount,
    },
    topVendors,
    soonestDeadlines,
    invoiceRows,
    termsDistribution,
    vendorSummary,
    paymentRuns,
    contextVersion: new Date().toISOString(),
  };
}

export function buildEmptyChatContext(page: string): ChatContext {
  return {
    page,
    invoiceSummary: {
      totalInvoices: 0,
      totalOutstanding: 0,
      totalPotentialSavings: 0,
      vendorCount: 0,
      earlyPayEligibleCount: 0,
      earlyPayEligibleAmount: 0,
    },
    topVendors: [],
    soonestDeadlines: [],
    invoiceRows: [],
    termsDistribution: [],
    vendorSummary: [],
    contextVersion: new Date().toISOString(),
  };
}
